import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { DocRenderer } from '@cyntler/react-doc-viewer';
import { renderAsync } from 'docx-preview';
import ViewerToolbar, { ViewMode } from './ViewerToolbar';
import { useViewerContainer } from './ViewerContainerContext';

const DocxViewer: DocRenderer = ({ mainState: { currentDocument } }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const viewerContext = useViewerContainer();
  const containerWidth = viewerContext.width;
  const containerHeight = viewerContext.height;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(viewerContext.initialViewMode || 'fit-width');
  const [zoomPx, setZoomPx] = useState<number>(800);
  const [numPages, setNumPages] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [toolbarHeight, setToolbarHeight] = useState(0);
  const [nativePageWidth, setNativePageWidth] = useState<number>(816);
  const pageDimensions = useRef<{ width: number; height: number }[]>([]);

  // Measure toolbar height once on mount
  useEffect(() => {
    if (scrollRef.current?.previousElementSibling) {
      setToolbarHeight(scrollRef.current.previousElementSibling.getBoundingClientRect().height);
    }
  }, []);

  const scrollAreaHeight = containerHeight - (toolbarHeight || 40);

  useEffect(() => {
    if (!currentDocument?.uri || !containerRef.current) return;

    setLoading(true);
    setError(null);

    const loadDocx = async () => {
      try {
        const response = await fetch(currentDocument.uri);
        const blob = await response.blob();
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
          await renderAsync(blob, containerRef.current, undefined, {
            inWrapper: true,
            ignoreWidth: false,
            ignoreHeight: false,
            breakPages: true,
          });
          // Detect pages and native size from rendered sections
          // Use requestAnimationFrame to ensure styles are applied before measuring
          requestAnimationFrame(() => {
            if (!containerRef.current) return;
            const sections = containerRef.current.querySelectorAll('section.docx');
            setNumPages(sections.length || 1);
            pageDimensions.current = Array.from(sections).map(s => {
              const el = s as HTMLElement;
              return { width: el.offsetWidth, height: el.offsetHeight };
            });
            if (sections.length > 0) {
              setNativePageWidth((sections[0] as HTMLElement).offsetWidth);
            }
          });
        }
      } catch (err) {
        setError(`Failed to render document: ${err}`);
      } finally {
        setLoading(false);
      }
    };

    loadDocx();
  }, [currentDocument?.uri]);

  // Compute content width based on view mode
  const currentPageDims = pageDimensions.current[currentPage - 1];
  const currentPageHeight = currentPageDims?.height || 1056;
  const currentPageWidth = currentPageDims?.width || nativePageWidth;

  let contentWidth: number;
  if (viewMode === 'fit-width') {
    contentWidth = containerWidth - 2;
  } else if (viewMode === 'single-page') {
    const widthByWidth = containerWidth - 2;
    const widthByHeight = (scrollAreaHeight - 2) * (currentPageWidth / currentPageHeight);
    contentWidth = Math.min(widthByWidth, widthByHeight);
  } else {
    contentWidth = zoomPx;
  }

  // CSS zoom factor: ratio of desired width to native rendered width
  const cssZoom = currentPageWidth > 0 ? contentWidth / currentPageWidth : 1;
  const displayZoomPct = Math.round(cssZoom * 100);

  const zoomStep = currentPageWidth * 0.25;
  const handleZoomOut = () => {
    const newWidth = Math.max(currentPageWidth * 0.25, contentWidth - zoomStep);
    setZoomPx(newWidth);
    handleSetViewMode('zoom');
  };
  const handleZoomIn = () => {
    const newWidth = Math.min(currentPageWidth * 5, contentWidth + zoomStep);
    setZoomPx(newWidth);
    handleSetViewMode('zoom');
  };
  const handleSetZoomPct = (pct: number) => {
    const newWidth = currentPageWidth * (pct / 100);
    setZoomPx(newWidth);
    handleSetViewMode('zoom');
  };

  // When leaving single-page mode, scroll to the page that was being viewed
  const pendingScrollPage = useRef<number | null>(null);

  const handleSetViewMode = useCallback((mode: ViewMode) => {
    if (viewMode === 'single-page' && mode !== 'single-page') {
      pendingScrollPage.current = currentPage;
    }
    setViewMode(mode);
  }, [viewMode, currentPage]);

  const isSinglePage = viewMode === 'single-page';

  // Scroll to pending page after switching from single-page to multi-page
  useEffect(() => {
    if (isSinglePage || !scrollRef.current || pendingScrollPage.current === null) return;
    const page = pendingScrollPage.current;
    pendingScrollPage.current = null;
    requestAnimationFrame(() => {
      const sections = containerRef.current?.querySelectorAll('section.docx');
      if (sections && sections[page - 1]) {
        sections[page - 1].scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    });
  }, [isSinglePage]);

  // Track visible page in fit-width/zoom modes via IntersectionObserver
  useEffect(() => {
    if (isSinglePage || !scrollRef.current || numPages <= 1) return;

    const sections = containerRef.current?.querySelectorAll('section.docx');
    if (!sections || sections.length === 0) return;

    const sectionList = Array.from(sections);
    const ratios = new Map<number, number>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const idx = sectionList.indexOf(entry.target as HTMLElement);
          if (idx >= 0) {
            ratios.set(idx + 1, entry.isIntersecting ? entry.intersectionRatio : 0);
          }
        }
        let bestPage = 0;
        let bestRatio = 0;
        for (const [page, ratio] of ratios) {
          if (ratio > bestRatio) {
            bestPage = page;
            bestRatio = ratio;
          }
        }
        if (bestPage > 0) setCurrentPage(bestPage);
      },
      { root: scrollRef.current, threshold: [0, 0.25, 0.5, 0.75, 1.0] }
    );

    sections.forEach(s => observer.observe(s));

    return () => observer.disconnect();
  }, [isSinglePage, numPages]);

  const navigatePage = (page: number) => {
    if (page < 1 || page > numPages) return;
    if (isSinglePage) {
      setCurrentPage(page);
    } else {
      // Just scroll — the IntersectionObserver will update currentPage
      const sections = containerRef.current?.querySelectorAll('section.docx');
      if (sections && sections[page - 1]) {
        sections[page - 1].scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: `${containerHeight}px` }}>
      <ViewerToolbar
        displayZoomPct={displayZoomPct}
        viewMode={viewMode}
        numPages={numPages}
        currentPage={currentPage}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onSetZoomPct={handleSetZoomPct}
        onSetViewMode={handleSetViewMode}
        onNavigatePage={navigatePage}
        extra={viewerContext.toolbarExtra}
      />
      <div
        ref={scrollRef}
        style={{
          overflow: 'auto',
          overflowX: isSinglePage ? 'hidden' : 'auto',
          flex: 1,
          background: '#e9ecef',
          display: 'flex',
          flexDirection: 'column',
          alignItems: isSinglePage ? 'center' : undefined,
          justifyContent: isSinglePage ? 'center' : undefined,
        }}
      >
        {loading && (
          <div className="text-center py-5"><Spinner animation="border" size="sm" /> Loading document...</div>
        )}
        {error && (
          <p className="text-danger p-3">{error}</p>
        )}
        <div style={{
          zoom: cssZoom,
          flexShrink: 0,
          margin: isSinglePage ? undefined : '0 auto',
          display: loading ? 'none' : undefined,
        }}>
          <style>{`
            .docx-wrapper { padding: 0 !important; background: transparent !important; }
            .docx-wrapper section.docx {
              margin-bottom: 8px !important;
              box-shadow: 0 1px 4px rgba(0,0,0,0.2) !important;
            }
            .docx-wrapper section.docx:last-child { margin-bottom: 0 !important; }
            ${isSinglePage ? `.docx-wrapper section.docx { display: none !important; } .docx-wrapper section.docx:nth-child(${currentPage}) { display: block !important; }` : ''}
          `}</style>
          <div ref={containerRef} />
        </div>
      </div>
    </div>
  );
};
DocxViewer.fileTypes = ['docx', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
DocxViewer.weight = 2;

export default DocxViewer;
